// SPDX-License-Identifier: BUSL-1.1

/**
 * Console → Archive deep-link affordance for a single run.
 *
 * Renders neutral cross-origin links from a run-inspection view (the
 * JobDetailSheet) to that run's read-only Archive views: the timeline +
 * evidence ("View run timeline") and the single-run auditor ("Open in
 * Auditor"). This is the Console's arrival path into the Archive (adoption
 * review F1); without it the Archive screens are reachable only by hand-typed
 * URL.
 *
 * Cross-surface affordance (ADR 009 — Console-Archive Integration Workflow):
 *  - The Archive is a separate origin, so these are real anchors
 *    (`target="_blank"` + `rel="noopener noreferrer"`), NOT react-router Links.
 *  - Wording is deliberately NEUTRAL. Archive brand vocabulary
 *    (EPISODE / CHANNEL / EVIDENCE CHAIN / WATCH) stays out of Console DOM text
 *    (bridge-001) — that vocabulary is native to the Archive's own surface, not
 *    the Console's.
 */

import type * as React from 'react';
import { ExternalLink } from 'lucide-react';
import { episodeUrl, auditorUrl } from '@/lib/archive-link';

/** Styled to match the dashboard external-link footer for a consistent affordance. */
function ArchiveLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-neutral-400 transition hover:bg-neutral-900 hover:text-neutral-200"
    >
      {children}
      <ExternalLink className="size-3" aria-hidden="true" />
    </a>
  );
}

/**
 * Deep-links to the Archive views for a run. `agentId` is the run's owning agent
 * (the Archive URL is agent-scoped — G1 authorizes the agent before resolving
 * the run); `runId` is the run's own id (job id or session id), which maps
 * verbatim to the Archive episode id.
 */
export function ArchiveRunLinks({
  agentId,
  runId,
}: {
  agentId: string;
  runId: string;
}): React.ReactElement {
  return (
    <nav aria-label="View this run in the Archive" className="flex items-center gap-1">
      <ArchiveLink href={episodeUrl(agentId, runId)}>View run timeline</ArchiveLink>
      <span className="text-xs text-neutral-700" aria-hidden="true">
        ·
      </span>
      <ArchiveLink href={auditorUrl(agentId, runId)}>Open in Auditor</ArchiveLink>
    </nav>
  );
}
